import { resolveSafeUserVisibleError } from '@formosa/shared/i18n/safeUserVisibleError';
import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';
import { normalizeCursorDateTime } from '../pagination/cursorDateTime';
import { coerceDateTime } from '../my-journeys/myJourneysModel';

const KNOWN_SOURCE_TYPES = new Set(['PLACE_POST', 'FOOD_POST', 'CUSTOM_LOCATION']);
const KNOWN_CATEGORIES = new Set(['CHECK_IN', 'FOOD', 'CUSTOM', 'MIXED']);

export const PERSONAL_MAP_SOURCE_FILTERS = Object.freeze({
  ALL: 'ALL',
  PLACE_POST: 'PLACE_POST',
  FOOD_POST: 'FOOD_POST',
  CUSTOM_LOCATION: 'CUSTOM_LOCATION',
});

export const PERSONAL_MAP_CATEGORY_FILTERS = Object.freeze({
  ALL: 'ALL',
  CHECK_IN: 'CHECK_IN',
  FOOD: 'FOOD',
  CUSTOM: 'CUSTOM',
});

export const PERSONAL_MAP_DEFAULT_LIMIT = 20;
export const PERSONAL_MAP_MAX_LIMIT = 50;

const text = (value, fallback = '') => (typeof value === 'string' && value.trim() ? value.trim() : fallback);

export function snowflakeId(value) {
  if (value == null || value === '') return null;
  const id = String(value);
  return /^[1-9]\d*$/.test(id) ? id : null;
}

function parseCoordinate(value) {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return null;
  return n;
}

export function normalizePersonalMapSource(raw) {
  const sourceTypeRaw = text(raw?.sourceType, '').toUpperCase();
  const sourceType = KNOWN_SOURCE_TYPES.has(sourceTypeRaw) ? sourceTypeRaw : 'UNKNOWN';
  return {
    sourceType,
    sourceId: snowflakeId(raw?.sourceId),
    occurredAt: coerceDateTime(raw?.occurredAt) || normalizeCursorDateTime(raw?.occurredAt) || null,
  };
}

export function categoryLabel(category, t) {
  const key = KNOWN_CATEGORIES.has(category) ? category : 'UNKNOWN';
  if (!t) return key;
  const map = {
    CHECK_IN: 'travel.personalMap.category.checkIn',
    FOOD: 'travel.personalMap.category.food',
    CUSTOM: 'travel.personalMap.category.custom',
    MIXED: 'travel.personalMap.category.mixed',
    UNKNOWN: 'travel.personalMap.category.place',
  };
  return t(map[key]);
}

export function sourceTypeLabel(sourceType, t) {
  if (!t) return sourceType;
  const map = {
    PLACE_POST: 'travel.personalMap.source.placePost',
    FOOD_POST: 'travel.personalMap.source.foodPost',
    CUSTOM_LOCATION: 'travel.personalMap.source.customLocation',
    UNKNOWN: 'travel.personalMap.source.unknown',
  };
  return t(map[sourceType] || map.UNKNOWN);
}

export function normalizePersonalMapEntry(raw, t) {
  const locationId = snowflakeId(raw?.locationId);
  if (!locationId) return null;

  const latitude = parseCoordinate(raw?.latitude);
  const longitude = parseCoordinate(raw?.longitude);
  const hasCoordinates = latitude != null
    && longitude != null
    && latitude >= -90 && latitude <= 90
    && longitude >= -180 && longitude <= 180;

  const categoryRaw = text(raw?.category, 'UNKNOWN').toUpperCase();
  const category = KNOWN_CATEGORIES.has(categoryRaw) ? categoryRaw : 'UNKNOWN';
  const statusRaw = text(raw?.status, 'ACTIVE').toUpperCase();
  const status = ['ACTIVE', 'HIDDEN', 'SOURCE_UNAVAILABLE', 'MODERATION_RESTRICTED'].includes(statusRaw)
    ? statusRaw
    : 'ACTIVE';

  const sources = Array.isArray(raw?.sources)
    ? raw.sources.map(normalizePersonalMapSource).filter((s) => s.sourceId != null)
    : [];

  const coverRef = raw?.preview?.coverFileReference;
  const coverUrl = coverRef ? resolveMemberMediaUrl(coverRef) : '';

  return {
    locationId,
    displayName: text(raw?.displayName, t ? t('travel.personalMap.unnamedPlace') : '地點'),
    latitude: hasCoordinates ? latitude : null,
    longitude: hasCoordinates ? longitude : null,
    hasCoordinates,
    category,
    status,
    hidden: Boolean(raw?.hidden) || status === 'HIDDEN',
    lastActivityAt: coerceDateTime(raw?.lastActivityAt)
      || normalizeCursorDateTime(raw?.lastActivityAt)
      || null,
    sources,
    preview: { coverUrl: coverUrl || null },
  };
}

export function normalizePersonalMapPage(payload, t) {
  const rawItems = Array.isArray(payload?.items) ? payload.items : [];
  const items = [];
  rawItems.forEach((raw) => {
    try {
      const entry = normalizePersonalMapEntry(raw, t);
      if (entry) items.push(entry);
    } catch (err) {
      // 非法 item 不得導致整頁崩潰
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[personal-map] skipped invalid entry', err);
      }
    }
  });

  const hasMore = Boolean(payload?.hasMore);
  const nextCursorLastActivityAt = normalizeCursorDateTime(payload?.nextCursorLastActivityAt)
    || coerceDateTime(payload?.nextCursorLastActivityAt)
    || null;
  const nextCursorLocationId = snowflakeId(payload?.nextCursorLocationId);
  const nextCursor = hasMore && nextCursorLastActivityAt && nextCursorLocationId
    ? { cursorLastActivityAt: nextCursorLastActivityAt, cursorLocationId: nextCursorLocationId }
    : null;

  return { items, hasMore, nextCursor };
}

/** Append with defensive locationId dedupe（不重新定義 backend dedupe）。 */
export function appendPersonalMapItems(current, incoming) {
  const seen = new Set((current || []).map((item) => item.locationId));
  const next = [...(current || [])];
  (incoming || []).forEach((item) => {
    if (!item?.locationId || seen.has(item.locationId)) return;
    seen.add(item.locationId);
    next.push(item);
  });
  return next;
}

export function toMapMarkers(entries) {
  return (entries || [])
    .filter((entry) => entry?.hasCoordinates)
    .map((entry) => ({
      key: entry.locationId,
      title: entry.displayName,
      locationName: entry.displayName,
      dayLabel: entry.category,
      summary: '',
      latitude: entry.latitude,
      longitude: entry.longitude,
    }));
}

/**
 * Bounds must be all-or-none；reject antimeridian (minLng > maxLng) and inverted lat.
 */
export function sanitizeBounds(bounds) {
  if (!bounds) return null;
  const {
    minLatitude, minLongitude, maxLatitude, maxLongitude,
  } = bounds;
  const values = [minLatitude, minLongitude, maxLatitude, maxLongitude];
  if (values.some((v) => v == null || v === '')) return null;
  const nums = values.map(Number);
  if (nums.some((n) => !Number.isFinite(n))) return null;
  const [minLat, minLng, maxLat, maxLng] = nums;
  if (minLat < -90 || maxLat > 90 || minLng < -180 || maxLng > 180) return null;
  if (minLat > maxLat) return null;
  // PM-1 不支援 antimeridian
  if (minLng > maxLng) return null;
  return {
    minLatitude: minLat,
    minLongitude: minLng,
    maxLatitude: maxLat,
    maxLongitude: maxLng,
  };
}

export function buildListParams({
  bounds = null,
  sourceFilter = PERSONAL_MAP_SOURCE_FILTERS.ALL,
  categoryFilter = PERSONAL_MAP_CATEGORY_FILTERS.ALL,
  includeHidden = false,
  cursor = null,
  limit = PERSONAL_MAP_DEFAULT_LIMIT,
} = {}) {
  const clamped = Math.min(
    Math.max(Number(limit) || PERSONAL_MAP_DEFAULT_LIMIT, 1),
    PERSONAL_MAP_MAX_LIMIT,
  );
  const params = { limit: clamped };
  const safeBounds = sanitizeBounds(bounds);
  if (safeBounds) Object.assign(params, safeBounds);
  if (sourceFilter && sourceFilter !== PERSONAL_MAP_SOURCE_FILTERS.ALL) {
    params.sourceTypes = sourceFilter;
  }
  if (categoryFilter && categoryFilter !== PERSONAL_MAP_CATEGORY_FILTERS.ALL) {
    params.category = categoryFilter;
  }
  if (includeHidden) params.includeHidden = true;
  if (cursor?.cursorLastActivityAt && cursor?.cursorLocationId) {
    params.cursorLastActivityAt = cursor.cursorLastActivityAt;
    params.cursorLocationId = cursor.cursorLocationId;
  }
  return params;
}

export function personalMapErrorMessage(cause, t) {
  if (typeof t === 'function') {
    return resolveSafeUserVisibleError(cause, t, 'travel.personalMap.loadFailed');
  }
  return resolveSafeUserVisibleError(cause, () => '無法載入我的地圖', 'travel.personalMap.loadFailed');
}

export function personalMapActionErrorMessage(cause, t) {
  if (typeof t === 'function') {
    return resolveSafeUserVisibleError(cause, t, 'travel.common.actionFailed');
  }
  return resolveSafeUserVisibleError(cause, () => '操作失敗，請稍後再試。', 'travel.common.actionFailed');
}
